import { useState, useMemo } from 'react';
import { Button } from './ui/button';
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from './ui/popover';
import { Mail, MoreHorizontal, CheckCircle } from 'lucide-react';
import { NCBReminderDialog } from './NCBReminderDialog';

/**
 * Compact side-menu for the "orders waiting for NCB clearance" needs-attention
 * card. Lists the affected orders; clicking "Send reminder" on a row opens the
 * shared preview-and-edit dialog (NCBReminderDialog). The dialog handles the
 * actual send so the menu never fires an email on its own.
 *
 * Reuses the same authoritative NCB status the rest of the app reads
 * (`order.ncb_clearance.status`) — no parallel state.
 */
export function NCBReminderMenu({ orderIds, orders, onSent }) {
    const [open, setOpen] = useState(false);
    const [dialogOrder, setDialogOrder] = useState(null);
    const [sent, setSent] = useState({}); // order_id → true after dialog reports success

    const rows = useMemo(() => {
        if (!Array.isArray(orderIds) || orderIds.length === 0) return [];
        const idSet = new Set(orderIds);
        const map = new Map((orders || []).map((o) => [o.id, o]));
        return orderIds
            .map((id) => map.get(id))
            .filter(Boolean)
            .map((o) => ({
                id: o.id,
                order_number: o.order_number,
                title: o.title,
                artist: o.artist,
                customer_email: o.customer_email,
                catalogue_number: o.catalogue_number,
                ncb_status: (o.ncb_clearance && o.ncb_clearance.status) || 'pending',
            }))
            // Filter again by ncb_status defensively — never surface cleared
            // rows in the reminder menu even if the parent list drifts.
            .filter((r) => r.ncb_status === 'pending' || r.ncb_status === 'follow_up_required')
            // Fall through to raw ID rows for anything the loaded orders list
            // doesn't include (e.g. an ID present in the admin bucket but
            // outside the currently loaded page). Rare but keeps the menu
            // honest.
            .concat(
                orderIds
                    .filter((id) => !map.has(id))
                    .map((id) => ({ id, order_number: null, title: '(not in current view)' }))
            );
    }, [orderIds, orders]);

    const openDialog = (row) => {
        setDialogOrder(row);
    };

    const handleSent = (result) => {
        if (dialogOrder) {
            setSent((prev) => ({ ...prev, [dialogOrder.id]: true }));
            if (onSent) onSent(dialogOrder.id, result);
        }
    };

    return (
        <>
            <Popover open={open} onOpenChange={setOpen}>
                <PopoverTrigger asChild>
                    <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7"
                        aria-label="Send NCB reminder"
                        data-testid="ncb-reminder-menu-trigger"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <MoreHorizontal className="h-4 w-4" />
                    </Button>
                </PopoverTrigger>
                <PopoverContent
                    align="end"
                    className="w-80 p-0"
                    onClick={(e) => e.stopPropagation()}
                    data-testid="ncb-reminder-menu-content"
                >
                    <div className="border-b border-slate-100 px-3 py-2">
                        <p className="text-xs font-semibold uppercase tracking-wide text-slate-600">
                            Send NCB reminder
                        </p>
                        <p className="text-[11px] text-slate-500 mt-0.5">
                            Opens a preview; the email only goes out after you approve it.
                        </p>
                    </div>
                    {rows.length === 0 ? (
                        <div className="px-3 py-4 text-xs text-slate-500 text-center">
                            No orders waiting on NCB clearance.
                        </div>
                    ) : (
                        <div className="max-h-72 overflow-y-auto py-1">
                            {rows.map((r) => {
                                const isSent = !!sent[r.id];
                                return (
                                    <div
                                        key={r.id}
                                        className="flex items-center gap-2 px-3 py-2 text-xs hover:bg-slate-50"
                                        data-testid={`ncb-reminder-row-${r.id}`}
                                    >
                                        <div className="flex-1 min-w-0">
                                            <div className="font-medium text-slate-700 truncate">
                                                {r.order_number ? `#${r.order_number}` : ''}
                                                {r.order_number && r.title ? ' — ' : ''}
                                                {r.title || ''}
                                            </div>
                                            {r.customer_email && (
                                                <div className="text-[11px] text-slate-500 truncate">
                                                    {r.customer_email}
                                                </div>
                                            )}
                                        </div>
                                        <Button
                                            variant={isSent ? 'ghost' : 'outline'}
                                            size="sm"
                                            className="h-7 px-2 text-[11px]"
                                            disabled={isSent || !r.order_number}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                openDialog(r);
                                            }}
                                            data-testid={`ncb-reminder-send-${r.id}`}
                                        >
                                            {isSent ? (
                                                <>
                                                    <CheckCircle className="h-3 w-3 mr-1" /> Sent
                                                </>
                                            ) : (
                                                <>
                                                    <Mail className="h-3 w-3 mr-1" /> Review & send
                                                </>
                                            )}
                                        </Button>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </PopoverContent>
            </Popover>

            {/* Shared preview-and-edit dialog — one instance driven by
                the clicked row. Mounted here (not inside each row) so a
                single Escape press can't dismiss the trigger too. */}
            <NCBReminderDialog
                open={!!dialogOrder}
                onOpenChange={(v) => { if (!v) setDialogOrder(null); }}
                orderId={dialogOrder?.id}
                orderNumber={dialogOrder?.order_number}
                orderTitle={dialogOrder?.title}
                onSent={handleSent}
            />
        </>
    );
}

export default NCBReminderMenu;
